import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/UsersPage';
import ResumesPage from './pages/ResumesPage';
import SettingsPage from './pages/SettingsPage';
import SurveysPage from './pages/SurveysPage';
import ScrapedVacanciesPage from './pages/ScrapedVacanciesPage';
import InternalVacanciesPage from './pages/InternalVacanciesPage';
import InternalVacancyDetailPage from './pages/InternalVacancyDetailPage';
import PageSEOSettingsPage from './pages/PageSEOSettingsPage';
import SEOSettingsPage from './pages/SEOSettingsPage';
import AITokenAnalyticsPage from './pages/AITokenAnalyticsPage';
import ReplicateCostPage from './pages/ReplicateCostPage';
import BlogListPage from './pages/BlogListPage';
import BlogArticleEditPage from './pages/BlogArticleEditPage';
import PromoCodesPage from './pages/PromoCodesPage';
import LogsPage from './pages/LogsPage';
import MentorDialogsPage from './pages/MentorDialogsPage';
import SalaryAnalysisPage from './pages/SalaryAnalysisPage';
import SalaryMarketPage from './pages/SalaryMarketPage';
import HypothesesFunnelPage from './pages/HypothesesFunnelPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import CompanyReviewsPage from './pages/CompanyReviewsPage';
import NotificationsAdminPage from './pages/NotificationsAdminPage';
import NotificationTemplatesPage from './pages/NotificationTemplatesPage';
import StoriesListPage from './pages/Stories/StoriesListPage';
import StoryEditPage from './pages/Stories/StoryEditPage';
import Layout from './components/Layout';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('admin_token');
  const adminUserStr = localStorage.getItem('admin_user');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Проверка роли
  if (adminUserStr) {
    try {
      const adminUser = JSON.parse(adminUserStr);
      const role = adminUser.role_name || adminUser.role;
      if (role !== 'admin' && role !== 'manager') {
        // Очищаем токен и редиректим на логин
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        return <Navigate to="/login" replace state={{ error: 'Требуются права администратора или менеджера' }} />;
      }
    } catch (e) {
      console.error('Ошибка парсинга admin_user:', e);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      return <Navigate to="/login" replace />;
    }
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/resumes"
          element={
            <ProtectedRoute>
              <Layout>
                <ResumesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/consultant"
          element={
            <ProtectedRoute>
              <Layout>
                <MentorDialogsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys"
          element={
            <ProtectedRoute>
              <Layout>
                <SurveysPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/promo-codes"
          element={
            <ProtectedRoute>
              <Layout>
                <PromoCodesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs"
          element={
            <ProtectedRoute>
              <Layout>
                <LogsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vacancies/scraped"
          element={
            <ProtectedRoute>
              <Layout>
                <ScrapedVacanciesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vacancies/internal"
          element={
            <ProtectedRoute>
              <Layout>
                <InternalVacanciesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vacancies/internal/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <InternalVacancyDetailPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Legacy routes для обратной совместимости */}
        <Route
          path="/scraped-vacancies"
          element={
            <ProtectedRoute>
              <Layout>
                <ScrapedVacanciesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seo/robots-sitemap"
          element={
            <ProtectedRoute>
              <Layout>
                <SEOSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seo/pages"
          element={
            <ProtectedRoute>
              <Layout>
                <PageSEOSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Legacy routes для обратной совместимости */}
        <Route
          path="/page-seo"
          element={
            <ProtectedRoute>
              <Layout>
                <PageSEOSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seo-settings"
          element={
            <ProtectedRoute>
              <Layout>
                <SEOSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <AITokenAnalyticsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/replicate-cost"
          element={
            <ProtectedRoute>
              <Layout>
                <ReplicateCostPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blog"
          element={
            <ProtectedRoute>
              <Layout>
                <BlogListPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blog/new"
          element={
            <ProtectedRoute>
              <Layout>
                <BlogArticleEditPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blog/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <BlogArticleEditPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salary-analysis"
          element={
            <ProtectedRoute>
              <Layout>
                <SalaryAnalysisPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salary-market"
          element={
            <ProtectedRoute>
              <Layout>
                <SalaryMarketPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hypotheses-funnel"
          element={
            <ProtectedRoute>
              <Layout>
                <HypothesesFunnelPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <Layout>
                <SupportTicketsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-reviews"
          element={
            <ProtectedRoute>
              <Layout>
                <CompanyReviewsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <NotificationsAdminPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications/templates"
          element={
            <ProtectedRoute>
              <Layout>
                <NotificationTemplatesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories"
          element={
            <ProtectedRoute>
              <Layout>
                <StoriesListPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <StoryEditPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster position="top-right" containerStyle={{ zIndex: 100010 }} />
    </Router>
  );
};

export default App;


